import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import LookalikeInteraction, { ResultDisplay } from '../components/LookalikeInteraction';
import { getModelHealth } from '../services/api';
import { trackClarityEvent } from '../utils/clarity';
import { ResultContext } from '../contexts/ResultContext';
import LiftCurveExplanation from '../components/lookalike/LiftCurveExplanation';
import { DownloadIcon } from '@heroicons/react/outline';
import RFMInteraction from '../components/RFMInteraction';
import VideoEmbed from '../components/VideoEmbed';
import MTAInteraction from '../components/MTAInteraction';
import '../styles/table.css'
import '../styles/charts.css'
import VOCAInteraction from '../components/VOCAInteraction';
import VOCAVisualization from '../components/VOCAVisualization';
import PaginationComponent from '../components/PaginationComponent';
import CustomerCards from '../components/CustomerCards';
import MMMInteraction from '../components/MMMInteraction';
import CLTVInteraction from '../components/CLTVInteraction';
import MMMInsights from '../components/MMMInsights';
import MmmTabs from '../components/MmmTabs';
import SpendingSummaryPanel from '../components/SpendingSummaryPanel';
import VOCAInsights from '../components/VOCAInsights';
import EchartDashbaord from '../components/EchartDashboard';
import { ECltvChartBgnbd, ECltvChartGgf, ECltvChartSegment, ECltvChartSpend } from '../components/CltvEchart';
import EChartLiftCurv from '../components/lookalike/EChartLiftCurv';
import ChannelAttributionChart from '../components/ChannelAttributionChart';
import StackedBarChart from '../components/StackedBarChart';
import FourPieCharts from '../components/FourPieCharts';
import CIDRInteraction from '../components/CIDRInteraction';
import HorizontalBarChart from '../components/CIDR/HorizontalBarChart';
import ColumnTypePieChart from '../components/CIDR/ColumnTypePieChart';
import DeduplicationSummaryPieChart from '../components/CIDR/DeduplicationSummaryPieChart';
import ContributingFieldsBarChart from '../components/CIDR/ContributingFieldsBarChart';
import DuplicateSimilarityHistogram from '../components/CIDR/DuplicateSimilarityHistogram';
import DuplicateClusterSizeHistogram from '../components/CIDR/DuplicateClusterSizeHistogram';
import CLTVSummary from '../components/CLTVSummary';
import CustomerInsights from "../components/CustomerInsights";
import CustomerProfiling from "../components/CustomerProfiling";
import DataQualityInteraction from '../components/DataQualityInteraction';
import EmailStatsChart from '../components/EmailStatsChartDataQuality';
import PhoneStatsChart from '../components/PhoneStatsChartDataQuality';
import QualityBreakdownPieChart from '../components/QualityBreakdownPieChart';

// --- Helper function to format timestamp string ---
const formatTimestamp = (timestamp) => {
  if (!timestamp || typeof timestamp !== 'string' || timestamp === 'N/A') return 'N/A';

  // Handle standard ISO format (e.g., from training updated_at)
  if (timestamp.includes('T')) {
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) { /* Fall through */ }
  }

  // Handle YYYYMMDD_HHMMSS format (e.g., from prediction metadata)
  const match = timestamp.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    try {
      // Create Date object (month is 0-indexed)
      const date = new Date(year, month - 1, day, hour, minute, second);
      return date.toLocaleString(); // Format based on user's locale
    } catch (e) {
      return timestamp; // Return original if parsing fails
    }
  }

  // Handle simple date strings (e.g., Tue Apr 29 12:54:02 2025)
  try {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString();
    }
  } catch (e) { /* Fall through */ }

  return timestamp; // Return original if no format matches
};
// --- End Helper ---

const ModelPage = () => {
  const { modelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { extraData } = location.state || {};
  const [activeTab, setActiveTab] = useState('overview');

  // Redirect away from Insights tab if on model ID 4 (Multi-Touch Attribution Model)
  useEffect(() => {
    if (modelId === '4' && activeTab === 'settings') {
      setActiveTab('overview');
    }
  }, [modelId, activeTab]);

  const [modelData, setModelData] = useState(null);


  const { result, setResult } = useContext(ResultContext);

  // Reset result state when modelId changes to prevent tabs from staying enabled across different models
  useEffect(() => {
    setResult(null);
  }, [modelId, setResult]);

  // State for dynamic metrics and training info, initialized with defaults
  const [dynamicMetrics, setDynamicMetrics] = useState({
    accuracy: 0,
    precision: 0,
    recall: 0,
    f1: 0, // Renamed from f1Score to match API response
    roc_auc: 0 // Renamed from auc
  });

  // Rename state to be more generic: holds info about the last completed run (train or predict)
  const [runInfo, setRunInfo] = useState({
    runType: 'N/A',
    lastRun: 'N/A',
    dataSize: 'N/A',
    duration: 'N/A',
    modelVersion: 'N/A',
    modelType: 'N/A' // Add modelType here too
  });

  // VOCA Analytics State
  const [vocaAnalytics, setVocaAnalytics] = useState(null);
  const [vocaLoading, setVocaLoading] = useState(false);



  // Placeholder data for models
  const modelTypes = {
    'lookalike': {
      name: 'Lookalike Model',
      description: 'A sophisticated tool designed to identify potential high-value customers by analyzing existing transaction data. It processes your current customer information to pinpoint individuals who exhibit purchasing behaviors similar to your most valuable clients.',
      accuracy: 92,
      status: 'Active',
      lastUpdated: '2023-10-28',
      trainedOn: '2023-10-15',
      features: [
        'Customer segmentation based on behavior',
        'Predictive analytics for acquisition',
        'Similar audience targeting',
        'Conversion probability scoring',
        'Customer value prediction'
      ],
      metrics: {
        precision: 0.89,
        recall: 0.92,
        f1Score: 0.90,
        auc: 0.94
      },
      visualization: {
        type: 'Customer Clusters'
      }
    },
    'rfm': {
      name: 'Recency Frequency Monetary Model',
      description: 'Recency value refers to the amount of time since a customers last interaction with a brand, which can include their last purchase, a visit to a website, use of a mobile app, a like on social media and more.',
      accuracy: 88,
      status: 'Active',
      lastUpdated: '2023-10-25',
      trainedOn: '2023-10-10',
      features: [
        'Purchase recency analysis',
        'Frequency patterns detection',
        'Monetary value assessment',
        'Customer lifecycle stage identification',
        'Engagement scoring'
      ],
      metrics: {
        precision: 0.87,
        recall: 0.84,
        f1Score: 0.85,
        auc: 0.91
      },
      visualization: {
        type: 'Heatmap Analysis'
      }
    },
    'cltv': {
      name: 'Customer Life Time Value Model',
      description: 'To predict the total revenue a customer is expected to generate throughout their entire relationship with a business. This allows companies to identify and prioritize high-value customers, optimize marketing spend, and personalize customer experiences.',
      accuracy: 88,
      status: 'Active',
      lastUpdated: '2023-10-25',
      trainedOn: '2023-10-10',
      features: [
        'Purchase recency analysis',
        'Frequency patterns detection',
        'Monetary value assessment',
        'Customer lifecycle stage identification',
        'Engagement scoring'
      ],
      metrics: {
        precision: 0.87,
        recall: 0.84,
        f1Score: 0.85,
        auc: 0.91
      },
      visualization: {
        type: 'Heatmap Analysis'
      }
    },
    'deduplication': {
      name: 'Customer Identity Resolution Model',
      description: 'To predict the customer profile duplication',
      accuracy: 88,
      status: 'Active',
      lastUpdated: '2023-10-25',
      trainedOn: '2023-10-10',
      features: [

      ],
      metrics: {
        precision: 0.87,
        recall: 0.84,
        f1Score: 0.85,
        auc: 0.91
      },
      visualization: {
        type: 'Heatmap Analysis'
      }
    },
    'attribution': {
      name: 'Attribution MARKOV Model',
      description: 'A mathematical framework that uses a stochastic process to model the customer journey. It represents a chain of stochastic events where the probability of each transition is only dependent on the state of the previous event.',
      accuracy: 76,
      status: 'In Training',
      lastUpdated: '2023-10-20',
      trainedOn: '2023-10-05',
      features: [
        'Multi-channel attribution',
        'Transition probability matrix',
        'Removal effect analysis',
        'Path-based valuation',
        'Cross-device tracking'
      ],
      metrics: {
        precision: 0.78,
        recall: 0.75,
        f1Score: 0.76,
        auc: 0.83
      },
      visualization: {
        type: 'Journey Map'
      }
    },
    'mta-attribution': {
      name: 'Multi-Touch Attribution Model',
      description: 'A recommendation system is an artificial intelligence or AI algorithm, usually associated with machine learning, that uses Big Data to suggest or recommend additional products to consumers.',
      accuracy: 94,
      status: 'Active',
      lastUpdated: '2025-05-16',
      trainedOn: '2025-05-16',
      features: [
        'Channel contribution analysis',
        'Touchpoint value assessment',
        'First-touch and last-touch comparison',
        'Linear, time-decay modeling',
        'Data-driven attribution'
      ],
      metrics: {
        precision: 0.93,
        recall: 0.91,
        f1Score: 0.92,
        auc: 0.96
      },
      visualization: {
        type: 'Attribution Flows'
      }
    },
    'vocaapi': {
      name: 'Voice of Customer Analytics',
      description: 'Voice of Customer (VoC) Analytics captures and analyzes customer feedback from various sources to uncover insights about customer needs, preferences, and experiences. It helps businesses enhance products, services, and overall customer satisfaction.',
      accuracy: 94,
      status: 'Active',
      lastUpdated: '2025-05-16',
      trainedOn: '2025-05-16',
      features: [
        'Channel contribution analysis',
        'Touchpoint value assessment',
        'First-touch and last-touch comparison',
        'Linear, time-decay modeling',
        'Data-driven attribution'
      ],
      metrics: {
        precision: 0.93,
        recall: 0.91,
        f1Score: 0.92,
        auc: 0.96
      },
      visualization: {
        type: 'Attribution Flows'
      }
    },
    'mmm-preprocessor': {
      name: 'Marketing Mix Modeling Analysis',
      description: 'Marketing Mix Modeling Analysis evaluates the performance and impact of marketing efforts across various media channels to optimize reach, engagement, and ROI. It helps identify the most effective platforms and strategies for audience targeting and campaign success.',
      features: [
        'Audience Reach',
        'Engagement Levels',
        'Conversion Rates',
        'Cost Efficiency'
      ],
      visualization: {
        type: 'Risk Heatmap'
      }
    },
    'data-quality': {
      name: 'Data Quality Model',
      description: 'Data Quality Models Des',
      features: [
        'Audience Reach',
        'Engagement Levels',
        'Conversion Rates',
        'Cost Efficiency'
      ],
      visualization: {
        type: 'Risk Heatmap'
      }
    }
  };

  // Mapping modelId to the keys in modelTypes and API endpoints
  const modelIdToKey = {
    '1': 'lookalike',
    '2': 'rfm',
    '3': 'cltv',
    '4': 'mta-attribution',
    '5': 'vocaapi',
    '6': 'mmm-preprocessor',
    '9': 'deduplication',
    '10': 'data-quality',

  };

  const key = modelIdToKey[modelId] || 'lookalike';
  console.log('key', key);

  // Function to handle tab changes and track in Clarity
  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      trackClarityEvent('model_tab_change', {
        modelId: modelId,
        modelType: key,
        previousTab: activeTab,
        newTab: tab
      });
      setActiveTab(tab);
    }
  };

  useEffect(() => {
    setModelData({ ...modelTypes[key], status: 'Loading...' });
    // Set initial model type based on key
    setRunInfo(prev => ({ ...prev, modelType: key }));

    //setModelData(extraData)
  }, [key, modelId]);





  useEffect(() => {
    if (!modelData) return;

    const checkModelHealth = async () => {
      try {
        const healthData = await getModelHealth(key);


        let status = 'Unknown';

        // Update model status based on health check
        if (healthData && healthData.status === 'healthy') {
          if (healthData.services?.model?.status === 'active') {
            status = 'Active';
          } else if (healthData.services?.model?.status === 'training') {
            status = 'In Training';
          }
        } else {
          status = 'Offline';
        }

        // Track model health status in Clarity
        trackClarityEvent('model_health_check', {
          modelId: modelId,
          modelType: key,
          status: status,
          healthStatus: healthData?.status || 'unknown'
        });

        // Update model data with the new status
        setModelData(prevData => ({
          ...prevData,
          status
        }));
      } catch (error) {
        console.error(`Error fetching health for ${key} model:`, error);

        // Track error in Clarity
        trackClarityEvent('model_health_error', {
          modelId: modelId,
          modelType: key,
          errorMessage: error.message
        });

        setModelData(prevData => ({
          ...prevData,
          status: 'Error'
        }));
      }
    };

    if (key != 'mta-attribution') {
      checkModelHealth();
    }

  }, [key, modelId, modelData?.name]); // Added modelData.name dependency

  // --- Callbacks for LookalikeInteraction ---
  const handleUploadComplete = useCallback((uploadResult) => {
    console.log("Upload complete callback received:", uploadResult);
    if (uploadResult?.file_details?.rows) {
      // Update dataSize based on the type of upload (training/testing)
      const sizeLabel = `${uploadResult.file_details.rows} records`;
      setRunInfo(prev => ({
        ...prev,
        dataSize: sizeLabel
      }));
      // Track upload completion
      trackClarityEvent('lookalike_upload_complete', {
        modelId: modelId,
        modelType: key,
        rows: uploadResult.file_details.rows,
        columns: uploadResult.file_details.columns,
        fileId: uploadResult.file_details.file_id
      });
    }
  }, [key, modelId]);



  const handleTrainingComplete = useCallback((finalResult) => {
    console.log("Training complete callback received:", finalResult);
    let lastRun = 'N/A';
    let duration = 'N/A';
    let metrics = null;

    if (finalResult?.task_status) {
      metrics = finalResult.task_status.metadata?.result_summary?.metrics;
      lastRun = finalResult.task_status.metadata?.result_summary?.completed_at || finalResult.task_status.updated_at || 'N/A';

      if (finalResult.task_status.created_at && finalResult.task_status.updated_at) {
        try {
          const start = new Date(finalResult.task_status.created_at).getTime();
          const end = new Date(finalResult.task_status.updated_at).getTime();
          const durationSeconds = Math.round((end - start) / 1000);
          if (durationSeconds > 0) {
            duration = `${durationSeconds} seconds`;
          }
        } catch (e) { /* ignore error */ }
      }
    }

    if (metrics) {
      setDynamicMetrics({
        accuracy: metrics.accuracy || 0,
        precision: metrics.precision || 0,
        recall: metrics.recall || 0,
        f1: metrics.f1 || 0,
        roc_auc: metrics.roc_auc || 0
      });
      // Track metric results
      trackClarityEvent('lookalike_training_metrics', {
        modelId: modelId,
        modelType: key,
        taskId: finalResult.task_id,
        ...metrics // Spread all metrics into the event
      });
    } else {
      // Reset metrics if training didn't return them
      setDynamicMetrics({ accuracy: 0, precision: 0, recall: 0, f1: 0, roc_auc: 0 });
    }

    // Update run info specifically for training
    setRunInfo(prev => ({
      ...prev,
      runType: 'Training',
      lastRun: lastRun,       // Keep raw data here
      duration: duration,
      modelVersion: 'N/A',
      modelType: finalResult?.task_status?.model_type || key // Update model type if available
    }));

  }, [key, modelId]);

  const primaryButtonClass = "px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0062cc] hover:bg-[#0052ac] disabled:opacity-50 disabled:cursor-not-allowed";

  const [custData, setcustData] = useState(null);

  // Cross-filtering disabled - using original RFMTreeMap component

  const handleCustomerComplete = useCallback((predictResult) => {
    console.log('ddd', predictResult);
    setcustData(predictResult)
  }, [key, modelId]);

  // VOCA training completion callback with analytics
  const handleVOCATrainingComplete = useCallback((vocaResult) => {
    console.log('VOCA training complete with analytics:', vocaResult);

    // If analytics are included, store them separately
    if (vocaResult?.analytics) {
      console.log('VOCA analytics received:', vocaResult.analytics);
      setVocaAnalytics(vocaResult.analytics);
      setVocaLoading(false);
    }

    // Store VOCA results in the result context
    setResult(vocaResult);

    // Track VOCA completion
    trackClarityEvent('voca_training_complete', {
      modelId: modelId,
      modelType: key,
      totalComments: vocaResult?.analysis_summary?.total_comments || 0,
      sentimentScore: vocaResult?.analysis_summary?.sentiment_distribution || {},
      analyticsGenerated: !!vocaResult?.analytics
    });
  }, [key, modelId]);

  // VOCA completion callback (for prediction/testing phase)
  const handleVOCAComplete = useCallback((vocaResult) => {
    console.log('VOCA analysis complete:', vocaResult);
    // Store VOCA results in the result context
    setResult(vocaResult);

    // Track VOCA completion
    trackClarityEvent('voca_analysis_complete', {
      modelId: modelId,
      modelType: key,
      totalComments: vocaResult?.analysis_summary?.total_comments || 0,
      sentimentScore: vocaResult?.analysis_summary?.sentiment_distribution || {}
    });
  }, [key, modelId]);

  // New callback for Prediction completion
  const handlePredictionComplete = useCallback((predictResult) => {
    console.log("Prediction complete callback received:", predictResult);
    let lastRun = 'N/A';
    let dataSize = 'N/A';
    let modelVersion = 'N/A';
    let testMetrics = null;

    if (predictResult) {
      lastRun = predictResult.metadata?.prediction_time || predictResult.completed_at || 'N/A';
      dataSize = predictResult.segment_stats?.total_customers ? `${predictResult.segment_stats.total_customers} customers` : 'N/A';
      modelVersion = predictResult.metadata?.model_version || 'N/A';
      testMetrics = predictResult.metrics?.test_metrics;
    }

    if (testMetrics) {
      setDynamicMetrics({ // Update metrics with test results
        accuracy: testMetrics.accuracy || 0,
        precision: testMetrics.precision || 0,
        recall: testMetrics.recall || 0,
        f1: testMetrics.f1 || 0,
        roc_auc: testMetrics.roc_auc || 0
      });
      // Track prediction metrics
      trackClarityEvent('lookalike_prediction_metrics', {
        modelId: modelId,
        modelType: key,
        ...testMetrics
      });
    } else {
      // Optionally reset metrics if prediction didn't return test metrics
      // setDynamicMetrics({ accuracy: 0, precision: 0, recall: 0, f1: 0, roc_auc: 0 });
    }

    // Update run info specifically for prediction
    setRunInfo(prev => ({
      ...prev,
      runType: 'Prediction',
      lastRun: lastRun,      // Keep raw data here
      dataSize: dataSize,
      duration: 'N/A', // Explicitly N/A for prediction
      modelVersion: modelVersion,
      modelType: key // Use the model key derived from ID
    }));

  }, [key, modelId]);
  // --- End Callbacks ---

  // Return early if model data is not yet loaded
  if (!modelData) return <div>Loading...</div>;


  // Format the last run time for display
  const displayLastRun = formatTimestamp(runInfo.lastRun);






  let parsedLiftData = [];

  if (result) {
    // --- Display Prediction Results ---

    const liftMetrics = result.lift_metrics;


    // --- Parse lift data ---

    if (liftMetrics?.lift_values) {
      try {
        const rawLiftData = JSON.parse(liftMetrics.lift_values);

        if (rawLiftData.index && rawLiftData['Lift (Total Sales)'] && rawLiftData['Cumulative Lift']) {
          parsedLiftData = rawLiftData.index.map((idx, i) => ({
            index: idx,
            'Lift (Total Sales)': rawLiftData['Lift (Total Sales)'][i] || 0,
            'Cumulative Lift': rawLiftData['Cumulative Lift'][i] || 0,
          }));
        }
      } catch (parseError) {
        console.error("Error parsing lift_values JSON:", parseError);
      }
    }
    // --- End Parse lift data ---
  }



  return (
    <div className="pt-24 pb-16 px-4 md:px-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <div className="flex items-center mb-8">
          <button
            onClick={() => {
              trackClarityEvent('model_navigation', {
                action: 'back_to_dashboard',
                modelId: modelId,
                modelType: key
              });
              navigate('/models');
            }}
            className="mr-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{modelData.name}</h1>

          </div>
        </div>



        <div className="mb-6">
          <nav className="flex space-x-6 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => handleTabChange('overview')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'overview'
                ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Overview
            </button>
            <button
              disabled={!result}
              onClick={() => handleTabChange('performance')}
              // className={`py-4 px-1 text-sm font-medium ${activeTab === 'performance'
              //   ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
              //   : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              //   }`}
              className={`py-4 px-1 text-sm font-medium ${!result // Use your 'isDisabled' variable here
                ? 'text-gray-400 cursor-not-allowed'
                : activeTab === 'performance'
                  ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Visualization
            </button>

            {modelId !== '4' && (
              <button
                disabled={!result}
                onClick={() => handleTabChange('settings')}
                // className={`py-4 px-1 text-sm font-medium ${activeTab === 'settings'
                //   ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
                //   : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                //   }`}
                className={`py-4 px-1 text-sm font-medium ${!result // Use your 'isDisabled' variable here
                  ? 'text-gray-400 cursor-not-allowed'
                  : activeTab === 'settings'
                    ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
              >
                Insights
              </button>
            )}

            <button
              onClick={() => handleTabChange('demo')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'demo'
                ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              View Demo
            </button>
          </nav>
        </div>

        <div style={{ display: activeTab === 'overview' ? 'block' : 'none' }}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-full"
              >
                {key === 'lookalike' ? (
                  <div  >
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Lookalike Model Interaction</h3>
                    <LookalikeInteraction
                      model={modelData}
                      extraData={extraData}
                      onTrainingComplete={handleTrainingComplete}
                      onUploadComplete={handleUploadComplete}
                      onPredictionComplete={handlePredictionComplete}
                    />
                  </div>
                ) :
                  key === 'rfm' ? (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Recency Frequency Monetory Model Interaction</h3>
                      <RFMInteraction
                        model={modelData}
                        extraData={extraData}
                        onTrainingComplete={handleTrainingComplete}
                        onUploadComplete={handleUploadComplete}
                        onPredictionComplete={handlePredictionComplete}
                        onCustomerComplete={handleCustomerComplete}
                      />
                    </div>
                  ) :
                    key === 'cltv' ? (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Customer Lifetime Value Model Interaction</h3>
                        <CLTVInteraction
                          model={modelData}
                          extraData={extraData}
                          onTrainingComplete={handleTrainingComplete}
                          onUploadComplete={handleUploadComplete}
                          onPredictionComplete={handlePredictionComplete}
                        />
                      </div>
                    ) :
                      key === 'data-quality' ? (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Data Quality Model Interaction</h3>
                          <DataQualityInteraction
                            model={modelData}
                            extraData={extraData}
                            onTrainingComplete={handleTrainingComplete}
                            onUploadComplete={handleUploadComplete}
                            onPredictionComplete={handlePredictionComplete}
                          />
                        </div>
                      ) :
                        key === 'deduplication' ? (
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Customer Identity Resolution Interaction</h3>
                            <CIDRInteraction
                              model={modelData}
                              extraData={extraData}
                              onTrainingComplete={handleTrainingComplete}
                              onUploadComplete={handleUploadComplete}
                              onPredictionComplete={handlePredictionComplete}
                            />
                          </div>
                        ) :
                          key === 'mmm-preprocessor' ? (
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Marketing Mix Modeling Model Interaction</h3>
                              <MMMInteraction
                                model={modelData}
                                extraData={extraData}
                                onTrainingComplete={handleTrainingComplete}
                                onUploadComplete={handleUploadComplete}
                                onPredictionComplete={handlePredictionComplete}
                              />
                            </div>
                          ) :
                            key === 'vocaapi' ? (
                              <div>
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Voice of Customer Analytics Model Interaction</h3>
                                <VOCAInteraction
                                  model={modelData}
                                  extraData={extraData}
                                  onTrainingComplete={handleVOCATrainingComplete}
                                  onUploadComplete={handleUploadComplete}
                                  onPredictionComplete={handleVOCAComplete}
                                />
                              </div>
                            ) :
                              key === 'mta-attribution' ? (
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Multi touch attribution model</h3>
                                  <MTAInteraction
                                    model={modelData}
                                    extraData={extraData}
                                    onTrainingComplete={handleTrainingComplete}
                                    onUploadComplete={handleUploadComplete}
                                    onPredictionComplete={handlePredictionComplete}
                                  />
                                </div>
                              ) :
                                (
                                  <>
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Model Visualization</h3>
                                    <div className="h-80 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg">
                                      <div className="text-center px-4">
                                        <p className="text-gray-400 dark:text-gray-500 mb-2">Placeholder for {modelData.visualization.type}</p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Upgrade your plan to access advanced visualizations</p>
                                      </div>
                                    </div>
                                    <div className="mt-4">
                                      <h4 className="font-medium text-gray-800 dark:text-white mb-2">Key Features</h4>
                                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {modelData.features.map((feature, index) => (
                                          <li key={index} className="flex items-start">
                                            <svg className="w-5 h-5 text-green-500 dark:text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </>
                                )}
              </motion.div>
            </div>

            <div>
              {key === 'lookalike' &&
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-6"
                >


                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Model Performance</h3>

                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Accuracy</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-white">{(dynamicMetrics.accuracy * 100).toFixed(0)}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.accuracy * 100}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Precision</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-white">{dynamicMetrics.precision.toFixed(2)}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.precision * 100}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Recall</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-white">{dynamicMetrics.recall.toFixed(2)}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.recall * 100}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-300">F1 Score</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-white">{dynamicMetrics.f1.toFixed(2)}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.f1 * 100}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-300">ROC AUC</span>
                        <span className="text-sm font-medium text-gray-800 dark:text-white">{dynamicMetrics.roc_auc.toFixed(2)}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.roc_auc * 100}%` }}></div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              }

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
              >
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                  {runInfo.runType === 'Prediction' ? 'Prediction Information' : 'Training Information'}
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Last Run Time</p>
                    <p className="text-gray-800 dark:text-white">{displayLastRun}</p>
                  </div>
                  {/* Show model version only if it was a prediction run */}
                  {runInfo.runType === 'Prediction' && runInfo.modelVersion !== 'N/A' && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Model Version Used</p>
                      <p className="text-gray-800 dark:text-white">{runInfo.modelVersion}</p>
                    </div>
                  )}
                  {/* Show model type (algorithm) */}
                  {(runInfo.modelType && runInfo.modelType !== 'N/A') && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Model Type / Algorithm</p>
                      <p className="text-gray-800 dark:text-white capitalize">{runInfo.modelType?.replace('_', ' ') || 'N/A'}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Input Data Size</p>
                    <p className="text-gray-800 dark:text-white">{runInfo.dataSize}</p>
                  </div>
                  {/* Show duration only if it was a training run */}
                  {runInfo.runType === 'Training' && runInfo.duration !== 'N/A' && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Run Duration</p>
                      <p className="text-gray-800 dark:text-white">{runInfo.duration}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {activeTab === 'performance' && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">

            <div>

              {
                key === 'mta-attribution' && result &&
                <div>
                  <ChannelAttributionChart data={result.results.attribution_summary} />
                  <div className='my-10'> </div>
                  <StackedBarChart data={result.results.attribution_summary} />
                  <div className='my-10'> </div>

                  <div className='my-10'> </div>
                  <FourPieCharts data={result.results.attribution_summary} />
                </div>
              }

              <div>

                {
                  key === 'deduplication' &&
                  <div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                      {/* Original Records */}
                      <div className="bg-blue-100 text-blue-800 dark:bg-gray-900 dark:text-white rounded-2xl shadow-md p-4 flex flex-col">
                        <h4 className="text-sm font-semibold mb-1">Original Records</h4>
                        <p className="text-xl font-bold">{result?.results?.summary_statistics['Original Records']}</p>
                      </div>

                      {/* Duplicate Records Identified */}
                      <div className="bg-red-100 text-red-800 dark:bg-gray-900 dark:text-white rounded-2xl shadow-md p-4 flex flex-col">
                        <h4 className="text-sm font-semibold mb-1">Duplicate Records Identified</h4>
                        <p className="text-xl font-bold">{result?.results?.summary_statistics['Duplicate Records Identified']}</p>
                      </div>

                      {/* Final Unique Records (Clusters) */}
                      <div className="bg-green-100 text-green-800 dark:bg-gray-900 dark:text-white rounded-2xl shadow-md p-4 flex flex-col">
                        <h4 className="text-sm font-semibold mb-1">Final Unique Records (Clusters)</h4>
                        <p className="text-xl font-bold">{result?.results?.summary_statistics['Final Unique Records (Clusters)']}</p>
                      </div>

                      {/* Deduplication Rate */}
                      <div className="bg-purple-100 text-purple-800 dark:bg-gray-900 dark:text-white rounded-2xl shadow-md p-4 flex flex-col">
                        <h4 className="text-sm font-semibold mb-1">Deduplication Rate</h4>
                        <p className="text-xl font-bold">{result?.results?.summary_statistics['Deduplication Rate']}</p>
                      </div>

                      {/* Avg Silhouette Score */}
                      <div className="bg-yellow-100 text-yellow-800 dark:bg-gray-900 dark:text-white rounded-2xl shadow-md p-4 flex flex-col">
                        <h4 className="text-sm font-semibold mb-1">Avg Silhouette Score (Clustering Quality)</h4>
                        <p className="text-xl font-bold">{result?.results?.summary_statistics['Avg Silhouette Score (Clustering Quality)']}</p>
                      </div>

                      {/* Key Fields Used */}
                      <div className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white rounded-2xl shadow-md p-4 col-span-1 sm:col-span-2 lg:col-span-3">
                        <h4 className="text-sm font-semibold mb-2">Key Fields Used</h4>
                        <p className="text-sm font-medium break-words leading-relaxed">
                          {result?.results?.summary_statistics['Key Fields Used']}
                        </p>
                      </div>
                    </div>

                    {/* <div className="flex flex-col lg:flex-row gap-4">
                      <div className="w-full lg:w-1/2">

                      </div>
                      <div className="w-full lg:w-1/2">

                      </div>
                    </div> */}

                    <div>


                      <DuplicateSimilarityHistogram />

                      <DuplicateClusterSizeHistogram />

                      <DeduplicationSummaryPieChart />

                    </div>

                  </div>

                }

                {/* {
                  key === 'deduplication' && <div className="flex flex-col md:flex-row w-full h-screen">

                    {iframeloading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-blue-600 border-opacity-75" />
                      </div>
                    )}

                    <iframe
                      src={result?.results?.visualizations['3_duplicate_similarity_scores']}
                      title="S3 HTML File"
                      width="100%"
                      height="400px"
                      className="w-full md:w-1/2 h-1/2 md:h-full border"
                      onLoad={() => setiframeLoading(false)}
                      style={{ visibility: iframeloading ? "hidden" : "visible" }}
                    />
                    <iframe
                      src={result?.results?.visualizations['6_duplicate_cluster_sizes_original']}
                      title="S3 HTML File2"
                      width="100%"
                      height="400px"
                      className="w-full md:w-1/2 h-1/2 md:h-full border"
                    />
                  </div>
                } */}





              </div>



              {key === 'mmm-preprocessor' && <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">MMM Metrics</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">Detailed analysis of model performance across different metrics and time periods.</p>
                <MmmTabs />
              </div>
              }

              {key === 'data-quality' && <div>
                <h1 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Email Metrics</h1>
                {/* <p className="text-gray-600 dark:text-gray-300 mb-6">{window.matchMedia('(prefers-color-scheme: dark)').matches}Detailed analysis of model performance across different metrics and time periods.</p> */}
                <EmailStatsChart data={result?.metadata?.summary?.email_metrics} />

                <h1 className="mt-10 text-lg font-semibold text-gray-800 dark:text-white mb-4">Phone Metrics</h1>
                <PhoneStatsChart data={result?.metadata?.summary?.phone_metrics} />
              </div>
              }

              {key === 'rfm' && <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Recency Frequency Monetary Metrics</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">Detailed analysis of model performance across different metrics and time periods.</p>

                {/* RFM Information Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* RFM Explanation Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/40 rounded-lg mr-4">
                        <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-blue-800 dark:text-blue-200">Understanding RFM Analysis</h4>
                        <p className="text-blue-600 dark:text-blue-400 text-sm">Recency • Frequency • Monetary</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-4">
                        <h5 className="font-semibold text-red-700 dark:text-red-400 mb-2">📅 Recency (R)</h5>
                        <p className="text-sm text-gray-700 dark:text-gray-300">How recently a customer made a purchase. Recent customers are more likely to respond to marketing campaigns.</p>
                      </div>

                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-4">
                        <h5 className="font-semibold text-green-700 dark:text-green-400 mb-2">🔄 Frequency (F)</h5>
                        <p className="text-sm text-gray-700 dark:text-gray-300">How often a customer makes purchases. Frequent buyers show higher engagement and loyalty to your brand.</p>
                      </div>

                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-4">
                        <h5 className="font-semibold text-purple-700 dark:text-purple-400 mb-2">💰 Monetary (M)</h5>
                        <p className="text-sm text-gray-700 dark:text-gray-300">How much money a customer has spent. High-value customers contribute more to your revenue and profit.</p>
                      </div>

                      <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-3 mt-4">
                        <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                          📊 <strong>RFM Scores:</strong> Each dimension is rated from 1 to 5, where 5 represents the highest value (most recent, most frequent, highest monetary value) and 1 represents the lowest value.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Customer Segments Card */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="p-3 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg mr-4">
                        <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-emerald-800 dark:text-emerald-200">Customer Segments</h4>
                        <p className="text-emerald-600 dark:text-emerald-400 text-sm">Based on RFM Analysis</p>
                      </div>
                    </div>

                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-semibold text-yellow-600 dark:text-yellow-500">🏆 Champions</h5>
                          <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 px-2 py-1 rounded">R = 1 and F = 5 and M = 5</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Recently active, frequent buyers who spend the most — your most valuable customers.</p>
                      </div>



                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-semibold text-blue-600 dark:text-blue-500">🤝 Loyal</h5>
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 px-2 py-1 rounded">R ≤ 2 and F ≥ 4 and M ≥ 4</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Repeat buyers who purchase often and consistently — responsive to engagement efforts.</p>
                      </div>

                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-semibold text-cyan-600 dark:text-cyan-500">✨ New Customer</h5>
                          <span className="text-xs bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-400 px-2 py-1 rounded"> R ≤ 2  and F = 1 and M ∈ 1-5</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">First-time buyers who purchased recently — nurture them to build loyalty.</p>
                      </div>

                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-semibold text-indigo-600 dark:text-indigo-500">🌟 Promising</h5>
                          <span className="text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-400 px-2 py-1 rounded">R ≤ 3 and F ≤ 2 and M ≤ 3</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Recent shoppers with lower frequency and spend — show potential for growth.</p>
                      </div>



                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-semibold text-orange-600 dark:text-orange-500">⚠️ At Risk</h5>
                          <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400 px-2 py-1 rounded">R ≥ 4 and F ≥ 3 and M ≥ 3</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Previously valuable customers who haven’t purchased in a while — need reactivation.</p>
                      </div>



                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-semibold text-red-700 dark:text-red-600">💔 Lost</h5>
                          <span className="text-xs bg-red-200 dark:bg-red-900/40 text-red-900 dark:text-red-400 px-2 py-1 rounded">R ≥ 4 and F ≤ 2 and M ≤ 2</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Inactive, low-spending customers — low engagement and low recent activity.</p>
                      </div>

                      <div className="bg-white/60 dark:bg-gray-800/40 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="font-semibold text-gray-700 dark:text-gray-600">🐻 Hibernating</h5>
                          <span className="text-xs bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-400 px-2 py-1 rounded">R ∈ [2–4], F ∈ [2–3], M ∈ [2–4]</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Customers with average engagement across all metrics — inactive but not completely lost.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <EchartDashbaord custData={custData} />





                <PaginationComponent />



              </div>}

              {key === 'vocaapi' && <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Voice of Customer Analytics Visualizations</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">Interactive charts and data tables showing customer feedback analysis across sentiment, topics, and actionable insights.</p>

                {/* VOCA Advanced Visualizations */}
                <VOCAVisualization
                  analyticsResults={vocaAnalytics}
                  loading={vocaLoading}
                />

                {/* VOCA Results Display */}
                {result?.analysis_summary && (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
                            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{result.analysis_summary.total_comments}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Total Comments</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center">
                          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mr-3">
                            <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{result.analysis_summary.sentiment_distribution.positive}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Positive</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center">
                          <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg mr-3">
                            <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{result.analysis_summary.sentiment_distribution.negative}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Negative</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center">
                          <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg mr-3">
                            <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{result.analysis_summary.urgency_distribution.high || 0}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">High Urgency</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sentiment Distribution Chart */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                      <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Sentiment Distribution</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-300">Positive</span>
                              <span className="text-sm font-medium text-green-600">{result.analysis_summary.sentiment_distribution.positive}</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${(result.analysis_summary.sentiment_distribution.positive / result.analysis_summary.total_comments) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="space-y-3 mt-4">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-300">Negative</span>
                              <span className="text-sm font-medium text-red-600">{result.analysis_summary.sentiment_distribution.negative}</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-red-500 h-2 rounded-full"
                                style={{ width: `${(result.analysis_summary.sentiment_distribution.negative / result.analysis_summary.total_comments) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="space-y-3 mt-4">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-300">Neutral</span>
                              <span className="text-sm font-medium text-gray-600">{result.analysis_summary.sentiment_distribution.neutral}</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-gray-500 h-2 rounded-full"
                                style={{ width: `${(result.analysis_summary.sentiment_distribution.neutral / result.analysis_summary.total_comments) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3">Intent Distribution</h5>
                          <div className="space-y-2">
                            {Object.entries(result.analysis_summary.intent_distribution).map(([intent, count]) => (
                              <div key={intent} className="flex justify-between items-center">
                                <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">{intent}</span>
                                <span className="text-sm font-medium text-gray-800 dark:text-white">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sample Individual Results */}
                    {result.individual_results && result.individual_results.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Sample Analysis Results</h4>
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                          {result.individual_results.slice(0, 5).map((item, index) => (
                            <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                              <div className="flex justify-between items-start mb-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.sentiment.overall === 'positive' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                  item.sentiment.overall === 'negative' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                  }`}>
                                  {item.sentiment.overall}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{item.intent.primary_intent}</span>
                              </div>
                              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{item.summary}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                "{item.analysis_metadata.original_comment.substring(0, 150)}..."
                              </p>
                              {item.key_insights.pain_points.length > 0 && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-red-600 dark:text-red-400">Pain Points: </span>
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    {item.key_insights.pain_points.join(', ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>}



              {
                result?.results?.plot_source_data && <div>


                  <ECltvChartBgnbd data={result?.results?.plot_source_data?.bgnbd_validation} />
                  <ECltvChartSpend data={result?.results?.plot_source_data?.cltv_validation} />
                  <ECltvChartGgf data={result?.results?.plot_source_data?.ggf_validation} />
                  <ECltvChartSegment data={result?.results?.plot_source_data?.cltv_segmentation} />


                </div>
              }


              {/* Lift Curve Visualization & Explanation */}
              {(result?.lift_curve_explanation /* Keep condition for layout */) && (
                <div className="mt-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <h4 className="font-semibold text-md mb-3 text-gray-800 dark:text-white">Lift Curve Analysis</h4>
                  {parsedLiftData.length > 0 ? (

                    <EChartLiftCurv data={parsedLiftData} />
                  ) : (
                    <p className="text-sm text-center text-gray-500 dark:text-gray-400 py-4">Could not load lift curve data.</p>
                  )}

                  {/* --- Render the new Explanation component --- */}
                  <div className="mt-4">
                    <LiftCurveExplanation />
                  </div>
                  {/* --- End new component rendering --- */}
                </div>
              )}


              {result?.preview_data && (
                <ResultDisplay title={`Sample Predictions (Showing ${result?.preview_data.summary?.total_samples || 'some'} customers)`} data={result?.preview_data.preview_html} />
              )}

              {/* Download Button */}
              {result?.predictions_path && (
                <div className="mt-6 text-center">
                  <a
                    href={result?.predictions_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${primaryButtonClass} inline-flex items-center`}
                  >
                    <DownloadIcon className="w-5 h-5 mr-2" />
                    Download Full Predictions (.csv)
                  </a>
                </div>
              )}

            </div>


          </div>
        )}

        {activeTab === 'settings' && (
          <div className=" p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700" >

            {key === 'cltv' && <div>

              {/* <div className="text-gray-600 dark:text-gray-300 mb-6" style={{ fontFamily: 'Roboto, sans-serif' }}>
                <div dangerouslySetInnerHTML={{ __html: result?.results?.generated_insights?.content }}></div>
              </div> */}

              <CLTVSummary />
            </div>
            }


            {
              key === 'deduplication' &&
              <div>

                <HorizontalBarChart />

                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="w-full lg:w-1/2">
                    <ContributingFieldsBarChart />
                  </div>
                  <div className="w-full lg:w-1/2">
                    <ColumnTypePieChart />
                  </div>
                </div>





              </div>
            }


            {key === 'rfm' && <div>

              <CustomerCards />


            </div>
            }


            {key === 'data-quality' && <div>

              <div className="p-4">
                <h3>Email Quality breakdown</h3>
                <QualityBreakdownPieChart data={result?.metadata?.summary?.email_metrics?.quality_breakdown} />
              </div>


            </div>
            }

            {key === 'mmm-preprocessor' && <div>
              <div className="flex flex-col md:flex-row m-6 bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
                {/* LEFT: summary */}

                <div className="md:w-1/3 w-full p-6 flex flex-col justify-evenly">

                  <div>
                    <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-100 mb-2">Top Channel - Google_SEM</h2>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {
                        result?.celery_status?.result?.insights?.top_spending_channels.channels.find(item => item.channel === "Google_SEM").formatted_spend

                      }
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-5 border-b">
                      Percentage :
                      {
                        result?.celery_status?.result?.insights?.top_spending_channels.channels.find(item => item.channel === "Google_SEM").percentage
                      } %</p>
                  </div>

                  <div>
                    <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-100 mb-2">Total Revenue</h2>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {result?.celery_status?.result?.insights?.total_revenue?.formatted}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-5 border-b">{result?.celery_status?.result?.insights?.total_revenue?.description}</p>
                  </div>

                  <div>
                    <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-100 mb-2">Total Spend</h2>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {result?.celery_status?.result?.insights?.top_spending_channels?.total_spend.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 border-b">{result?.celery_status?.result?.insights?.top_spending_channels?.description}</p>
                  </div>

                </div>
                {/* RIGHT: chart */}
                <div className="md:w-2/3 w-full ">
                  <SpendingSummaryPanel data={result?.celery_status?.result?.insights?.top_spending_channels || []} />

                </div>
              </div>


              <MMMInsights insights={result?.celery_status?.result?.insights || {}} />


            </div>
            }

            {key === 'vocaapi' && (
              <div>
                <VOCAInsights
                  analyticsResults={vocaAnalytics}
                  loading={vocaLoading}
                />
              </div>
            )}

            {/* Key Takeaways */}
            {/* {result?.key_takeaways && (
              <ResultDisplay title="Key Takeaways" data={result?.key_takeaways} />
            )}

            {result?.customer_profile_insights && (
              <ResultDisplay title="Customer Profile Insights" data={result?.customer_profile_insights} />
            )} */}

            {result?.key_takeaways && (
              <div className="space-y-8">
                <CustomerInsights />
                <CustomerProfiling />
              </div>
            )}


          </div>
        )}

        {activeTab === 'demo' && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">




            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">{extraData?.name}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                {extraData?.youtube_url && <div>
                  <div>

                    <VideoEmbed url={extraData?.youtube_url} />
                  </div>
                </div>}
              </div>


              <div className="text-gray-600 dark:text-gray-300 mb-6" style={{ fontFamily: 'Roboto, sans-serif' }}>
                <div dangerouslySetInnerHTML={{ __html: extraData?.description }}></div>
              </div>

            </div>




          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ModelPage;
